import { Link } from "react-router-dom";
import { VerdictShield } from "../glyphs";
import type { Readiness } from "../types";

/** A small up/down delta against the previous comparable run. */
function Delta({ value, lowerIsBetter }: { value: number; lowerIsBetter?: boolean }) {
  if (value === 0) return <span className="text-xs opacity-50">no change</span>;
  const good = lowerIsBetter ? value < 0 : value > 0;
  const sign = value > 0 ? "+" : "";
  return (
    <span className={`text-xs font-medium ${good ? "text-success" : "text-error"}`}>
      {sign}
      {Number.isInteger(value) ? value : value.toFixed(1)}
    </span>
  );
}

function Stat({
  label,
  value,
  delta,
  lowerIsBetter,
}: {
  label: string;
  value: string;
  delta?: number;
  lowerIsBetter?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide opacity-50">{label}</dt>
      <dd className="flex items-baseline gap-2">
        <span className="text-2xl font-bold tabular-nums">{value}</span>
        {delta !== undefined && <Delta value={delta} lowerIsBetter={lowerIsBetter} />}
      </dd>
    </div>
  );
}

/**
 * The release-readiness band: the shipping question answered in seconds.
 *
 * The shield leads; the reason follows; then the four numbers that matter, each
 * with its move against the previous comparable run. This is success criterion
 * #1 of the redesign — a verdict at the top, not a table.
 */
export default function ReadinessBand({ readiness }: { readiness: Readiness }) {
  const { current, previous, delta } = readiness;
  const reason =
    current.reasons.length > 0
      ? current.reasons.join("; ")
      : "Every in-scope target met policy.";

  return (
    <section className="card bg-base-200">
      <div className="card-body gap-4">
        <div className="flex flex-wrap items-start gap-4">
          <VerdictShield verdict={current.decision} size="lg" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-baseline gap-2">
              <h2 className="text-xl font-bold">
                {current.decision === "go" ? "Ready to ship" : "Not ready to ship"}
              </h2>
              <Link to={`/runs/${current.run_id}`} className="link link-hover text-sm opacity-70">
                Run #{current.run_number} · {current.scope_kind.replaceAll("_", " ")}{" "}
                {current.scope_ref}
              </Link>
            </div>
            <p className={`text-sm mt-1 ${current.decision === "go" ? "opacity-70" : "text-error"}`}>
              {reason}
            </p>
          </div>
        </div>

        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat
            label="Coverage"
            value={`${current.coverage_pct.toFixed(1)}%`}
            delta={delta?.coverage_pct}
          />
          <Stat label="Gaps" value={String(current.gaps_count)} delta={delta?.gaps_count} lowerIsBetter />
          <Stat
            label="Regressions"
            value={String(current.unresolved_regressions)}
            delta={delta?.unresolved_regressions}
            lowerIsBetter
          />
          <Stat
            label="Blocked"
            value={String(current.blocked_count)}
            delta={delta?.blocked_count}
            lowerIsBetter
          />
        </dl>

        <p className="text-xs opacity-50">
          {previous
            ? `Compared with run #${previous.run_number} of the same scope.`
            : "No earlier run of this scope to compare against."}
        </p>
      </div>
    </section>
  );
}
